import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, fullName, formatDate } from '../lib/api';
import { PageHeader, EmptyState, MiniStat } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ListChecks, Plus, Trash2, FileText, Eye, FilePen, Search, Loader2, Save, X, FileEdit, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { normalizaTexto } from '../lib/utils';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import PdfLangButton from '../components/PdfLangButton';
import { PatientCombobox } from '../components/PatientCombobox';
import { useAuth } from '../lib/auth';
import { isManagement } from '../lib/roles';
import { error as logError } from '../lib/logger';
import { AvisoHojaSugerida, pedirHojaSugerida } from '../components/quirofano/PostopQuickDialog';
import VocabularioHeridaDialog from '../components/quirofano/VocabularioHeridaDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ---------- Helpers ----------

function emptySection() {
  return { title: '', items: [''], body: '' };
}

function normalizeSections(sections) {
  // El backend acepta items[] y opcionalmente body.
  return (sections || []).map(s => ({
    title: s.title || '',
    items: (s.items || []).filter(x => x !== null && x !== undefined),
    body: s.body || '',
  }));
}

// ---------- Editor de plantilla ----------

function TemplateEditorDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const empty = { code: '', name: '', sections: [emptySection()], is_default: false, conceptos: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        code: initial.code || '',
        name: initial.name || '',
        sections: normalizeSections(initial.sections).length ? normalizeSections(initial.sections) : [emptySection()],
        is_default: !!initial.is_default,
        conceptos: (initial.conceptos || []).join(', '),
      });
    } else {
      setForm(empty);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id]);

  const setSection = (idx, key, value) => {
    setForm(f => ({ ...f, sections: f.sections.map((s, i) => i === idx ? { ...s, [key]: value } : s) }));
  };
  const setItem = (sIdx, iIdx, value) => {
    setForm(f => ({
      ...f,
      sections: f.sections.map((s, i) => i === sIdx
        ? { ...s, items: s.items.map((it, j) => j === iIdx ? value : it) }
        : s),
    }));
  };
  const addSection = () => setForm(f => ({ ...f, sections: [...f.sections, emptySection()] }));
  const removeSection = (idx) => setForm(f => ({ ...f, sections: f.sections.filter((_, i) => i !== idx) }));
  const addItem = (sIdx) => setForm(f => ({
    ...f,
    sections: f.sections.map((s, i) => i === sIdx ? { ...s, items: [...s.items, ''] } : s),
  }));
  const removeItem = (sIdx, iIdx) => setForm(f => ({
    ...f,
    sections: f.sections.map((s, i) => i === sIdx
      ? { ...s, items: s.items.filter((_, j) => j !== iIdx) }
      : s),
  }));

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      return toast.error('Código y nombre son obligatorios');
    }
    setSaving(true);
    try {
      const sections = form.sections
        .filter(s => s.title.trim() || s.items.some(it => it.trim()) || s.body.trim())
        .map(s => ({
          title: s.title.trim(),
          items: s.items.map(it => (it || '').trim()).filter(Boolean),
          body: s.body.trim(),
        }));
      const conceptos = String(form.conceptos || '').split(',').map((c) => c.trim()).filter(Boolean);
      const payload = { code: form.code.trim(), name: form.name.trim(), sections, is_default: form.is_default, conceptos };
      if (isEdit) {
        await api.put(`/api/postoperatorios/templates/${initial.id}`, payload);
        toast.success('Plantilla actualizada');
      } else {
        await api.post('/api/postoperatorios/templates', payload);
        toast.success('Plantilla creada');
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const onPreview = async () => {
    setPreviewing(true);
    try {
      const sections = form.sections
        .map(s => ({
          title: s.title.trim(),
          items: s.items.map(it => (it || '').trim()).filter(Boolean),
          body: s.body.trim(),
        }))
        .filter(s => s.title || s.items.length || s.body);
      const res = await api.post('/api/postoperatorios/templates/preview-pdf', {
        sections,
        patient_name: 'Paciente Ejemplo',
        surgery_name: 'Cirugía Ejemplo',
        surgery_date: new Date().toISOString().slice(0, 10),
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(url);
    } catch (e) {
      toast.error('No se pudo previsualizar');
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="postop-template-editor-dialog">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Editar plantilla' : 'Nueva plantilla'} de postoperatorio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="postop-tpl-code">Código (único)</Label>
                <Input
                  id="postop-tpl-code"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="postop-estandar"
                  disabled={isEdit}
                  data-testid="postop-tpl-code"
                />
              </div>
              <div>
                <Label htmlFor="postop-tpl-name">Nombre</Label>
                <Input
                  id="postop-tpl-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Cirugía Plástica (Estándar)"
                  data-testid="postop-tpl-name"
                />
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_default}
                onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                data-testid="postop-tpl-default"
              />
              Usar esta plantilla por defecto al generar postoperatorios desde quirófano
            </label>

            <div>
              <Label>Tipos de cita que cubre (códigos, separados por comas)</Label>
              <Input
                value={form.conceptos}
                onChange={(e) => setForm({ ...form, conceptos: e.target.value })}
                placeholder="p. ej. cirugia-mayor, retoque"
                data-testid="postop-tpl-conceptos"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Al dar el postoperatorio de una cita de estos tipos se propone esta hoja. Una intervención
                de un tipo que no cubre ninguna hoja lleva la de por defecto.
              </p>
            </div>

            <div className="space-y-3">
              <div className="text-sm font-semibold text-[#13231E]">Secciones</div>
              {form.sections.map((s, sIdx) => (
                <div key={`sec-${sIdx}`} className="border border-border rounded-md p-3 bg-[#F0F6F2]/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Input
                      value={s.title}
                      onChange={(e) => setSection(sIdx, 'title', e.target.value)}
                      placeholder="Título de la sección (ej: HOY)"
                      className="font-semibold"
                      data-testid={`postop-tpl-section-title-${sIdx}`}
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeSection(sIdx)} title="Eliminar sección" data-testid={`postop-tpl-section-remove-${sIdx}`}>
                      <X className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    {s.items.map((it, iIdx) => (
                      <div key={`it-${sIdx}-${iIdx}`} className="flex items-start gap-2">
                        <span className="text-muted-foreground text-sm mt-2">•</span>
                        <Textarea
                          value={it}
                          onChange={(e) => setItem(sIdx, iIdx, e.target.value)}
                          placeholder="Instrucción o punto…"
                          rows={1}
                          className="flex-1 min-h-[36px] resize-y"
                          data-testid={`postop-tpl-item-${sIdx}-${iIdx}`}
                        />
                        <Button type="button" variant="ghost" size="icon" className="mt-0.5" onClick={() => removeItem(sIdx, iIdx)} title="Eliminar línea">
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => addItem(sIdx)} className="mt-1" data-testid={`postop-tpl-additem-${sIdx}`}>
                      <Plus className="w-3 h-3 mr-1" /> Añadir línea
                    </Button>
                  </div>
                  <div className="mt-2">
                    <Label className="text-xs">Texto adicional (opcional)</Label>
                    <Textarea
                      value={s.body}
                      onChange={(e) => setSection(sIdx, 'body', e.target.value)}
                      placeholder="Párrafo de texto libre debajo de la lista"
                      rows={2}
                      data-testid={`postop-tpl-body-${sIdx}`}
                    />
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addSection} data-testid="postop-tpl-addsection">
                <Plus className="w-4 h-4 mr-1" /> Añadir sección
              </Button>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={onPreview} disabled={previewing} data-testid="postop-tpl-preview-btn">
              {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />} Previsualizar
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="postop-tpl-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PdfPreviewDialog
        open={!!previewUrl}
        onOpenChange={(v) => { if (!v) { URL.revokeObjectURL(previewUrl); setPreviewUrl(''); } }}
        url={previewUrl}
        filename="postop_preview.pdf"
        title="Previsualización de plantilla"
      />
    </>
  );
}

// ---------- Editor por paciente (documento individual) ----------

function DocumentEditorDialog({ open, onOpenChange, doc, templates, onSaved }) {
  const [sections, setSections] = useState([]);
  const [extraNotes, setExtraNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !doc) return;
    // El documento tiene custom_sections (si ya fueron editadas) o usa las de la plantilla
    const source = doc.custom_sections && doc.custom_sections.length
      ? doc.custom_sections
      : (templates.find(t => t.id === doc.template_id)?.sections || []);
    setSections(normalizeSections(source).map(s => ({
      ...s,
      items: s.items.length ? s.items : [''],
    })));
    setExtraNotes(doc.extra_notes || '');
  }, [open, doc, templates]);

  const setSection = (idx, key, value) => {
    setSections(prev => prev.map((s, i) => i === idx ? { ...s, [key]: value } : s));
  };
  const setItem = (sIdx, iIdx, value) => {
    setSections(prev => prev.map((s, i) => i === sIdx
      ? { ...s, items: s.items.map((it, j) => j === iIdx ? value : it) }
      : s));
  };
  const removeItem = (sIdx, iIdx) => {
    setSections(prev => prev.map((s, i) => i === sIdx
      ? { ...s, items: s.items.filter((_, j) => j !== iIdx) }
      : s));
  };
  const addItem = (sIdx) => {
    setSections(prev => prev.map((s, i) => i === sIdx ? { ...s, items: [...s.items, ''] } : s));
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleanSections = sections
        .map(s => ({
          title: s.title.trim(),
          items: s.items.map(it => (it || '').trim()).filter(Boolean),
          body: (s.body || '').trim(),
        }))
        .filter(s => s.title || s.items.length || s.body);
      await api.put(`/api/postoperatorios/documents/${doc.id}`, {
        custom_sections: cleanSections,
        extra_notes: extraNotes,
      });
      toast.success('Postoperatorio actualizado');
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="postop-doc-editor-dialog">
        <DialogHeader>
          <DialogTitle>Personalizar postoperatorio — {doc.patient_name}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground mb-2">
          Cirugía: <strong>{doc.surgery_name || '—'}</strong> · Fecha: <strong>{doc.surgery_date || '—'}</strong>
          <span className="ml-2 italic">Quita lo que no aplique a este paciente o añade indicaciones específicas.</span>
        </div>
        <div className="space-y-3">
          {sections.map((s, sIdx) => (
            <div key={`docsec-${sIdx}`} className="border border-border rounded-md p-3 bg-[#F0F6F2]/50">
              <Input
                value={s.title}
                onChange={(e) => setSection(sIdx, 'title', e.target.value)}
                className="font-semibold mb-2"
                placeholder="Título de la sección"
              />
              <div className="space-y-1.5">
                {s.items.map((it, iIdx) => (
                  <div key={`docit-${sIdx}-${iIdx}`} className="flex items-start gap-2">
                    <span className="text-muted-foreground text-sm mt-2">•</span>
                    <Textarea
                      value={it}
                      onChange={(e) => setItem(sIdx, iIdx, e.target.value)}
                      rows={1}
                      className="flex-1 min-h-[36px] resize-y"
                      data-testid={`postop-doc-item-${sIdx}-${iIdx}`}
                    />
                    <Button type="button" variant="ghost" size="icon" className="mt-0.5" onClick={() => removeItem(sIdx, iIdx)}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addItem(sIdx)}>
                  <Plus className="w-3 h-3 mr-1" /> Añadir línea
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3">
          <Label htmlFor="postop-doc-extra">Notas adicionales para este paciente</Label>
          <Textarea
            id="postop-doc-extra"
            value={extraNotes}
            onChange={(e) => setExtraNotes(e.target.value)}
            placeholder="Indicaciones específicas para este paciente"
            rows={3}
            data-testid="postop-doc-extra"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="postop-doc-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Diálogo para crear documento manual ----------

function NewDocumentDialog({ open, onOpenChange, templates, surgeons, patients, onSaved }) {
  const [patientId, setPatientId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [surgeonId, setSurgeonId] = useState('');
  const [surgeryDate, setSurgeryDate] = useState(new Date().toISOString().slice(0, 10));
  const [surgeryName, setSurgeryName] = useState('');
  const [saving, setSaving] = useState(false);
  // null (sin paciente) | { cargando } | { code, porQue } | { error }
  const [sugerencia, setSugerencia] = useState(null);
  // Cada paciente elegido es una pregunta nueva: la respuesta de uno
  // anterior que llegue tarde no puede elegir la hoja del de ahora.
  const turno = React.useRef(0);

  useEffect(() => {
    if (open) {
      turno.current += 1;
      setPatientId('');
      // Nada elegido hasta saber de quién es (23/9/2026). Antes salía la de
      // por defecto —la de una intervención concreta, con su medicación—
      // antes incluso de elegir paciente, y a un paciente de otra intervención
      // se le daba esa si nadie miraba el desplegable. Es la
      // tercera puerta para dar un postoperatorio; el parte y la ficha ya
      // preguntaban al servidor (informe del 22/9, M1).
      setTemplateId('');
      setSugerencia(null);
      setSurgeonId(surgeons.find(s => s.is_default)?.id || surgeons[0]?.id || '');
      setSurgeryDate(new Date().toISOString().slice(0, 10));
      setSurgeryName('');
    }
  }, [open, templates, surgeons]);

  // Al elegir el paciente, la hoja que le toca la dice el servidor, con la
  // misma regla que el parte y la ficha. Mientras contesta no hay ninguna
  // elegida; si falla, se dice y se elige a mano. Lo que se elija a mano
  // mientras tanto se respeta.
  const elegirPaciente = (id) => {
    setPatientId(id);
    setTemplateId('');
    turno.current += 1;
    const este = turno.current;
    if (!id) {
      setSugerencia(null);
      return;
    }
    setSugerencia({ cargando: true });
    pedirHojaSugerida(null, id).then((sug) => {
      if (este !== turno.current) return;
      setSugerencia(sug);
      const suya = sug?.code && templates.find((t) => t.code === sug.code);
      setTemplateId((aMano) => aMano || suya?.id || '');
    });
  };

  const submit = async () => {
    if (!patientId) return toast.error('Selecciona un paciente');
    if (!templateId) return toast.error('Selecciona una plantilla');
    setSaving(true);
    try {
      await api.post('/api/postoperatorios/documents', {
        patient_id: patientId,
        template_id: templateId,
        surgeon_id: surgeonId || null,
        surgery_date: surgeryDate || null,
        surgery_name: surgeryName || null,
      });
      toast.success('Postoperatorio creado');
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al crear');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="postop-new-doc-dialog">
        <DialogHeader>
          <DialogTitle>Nuevo postoperatorio</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Paciente</Label>
            <PatientCombobox
              patients={patients}
              value={patientId}
              onChange={elegirPaciente}
              data-testid="postop-new-doc-patient"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Plantilla base</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger data-testid="postop-new-doc-template"><SelectValue placeholder="Elige la pauta de esta cirugía" /></SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? ' (por defecto)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {patientId ? (
                <AvisoHojaSugerida
                  sugerencia={sugerencia}
                  templateId={templateId}
                  templates={templates}
                  prefijo="postop-new-doc"
                />
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  Al elegir el paciente se propone la hoja de su última intervención.
                </p>
              )}
            </div>
            <div>
              <Label>Cirujano</Label>
              <Select value={surgeonId} onValueChange={setSurgeonId}>
                <SelectTrigger data-testid="postop-new-doc-surgeon"><SelectValue placeholder="Cirujano" /></SelectTrigger>
                <SelectContent>
                  {surgeons.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fecha de la cirugía</Label>
              <Input type="date" value={surgeryDate} onChange={(e) => setSurgeryDate(e.target.value)} data-testid="postop-new-doc-date" />
            </div>
            <div>
              <Label>Cirugía (texto libre)</Label>
              <Input value={surgeryName} onChange={(e) => setSurgeryName(e.target.value)} placeholder="ej.: nombre de la intervención" data-testid="postop-new-doc-surgery" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="postop-new-doc-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Página principal ----------

export default function PostopPage() {
  const { user } = useAuth();
  const mgmt = isManagement(user);
  const [templates, setTemplates] = useState([]);
  const [docs, setDocs] = useState([]);
  const [patients, setPatients] = useState([]);
  const [surgeons, setSurgeons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingTpl, setEditingTpl] = useState(null);
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [newDocOpen, setNewDocOpen] = useState(false);
  const [preview, setPreview] = useState({ open: false, url: '', filename: '', title: '' });
  const [vocabularioOpen, setVocabularioOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [t, d, p, sg] = await Promise.all([
        api.get('/api/postoperatorios/templates'),
        api.get('/api/postoperatorios/documents'),
        api.get('/api/patients'),
        api.get('/api/surgeons'),
      ]);
      setTemplates(t.data);
      setDocs(d.data);
      setPatients(p.data);
      setSurgeons(sg.data);
    } catch (e) {
      logError('postop fetchAll', e?.message || e);
      toast.error('Error cargando postoperatorios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredDocs = useMemo(() => {
    const q = normalizaTexto(search.trim());
    if (!q) return docs;
    return docs.filter(d =>
      normalizaTexto(d.patient_name).includes(q)
      || normalizaTexto(d.surgery_name).includes(q)
      || normalizaTexto(d.template_name).includes(q)
    );
  }, [docs, search]);

  const openPdf = (doc) => {
    setPreview({
      open: true,
      url: `${BACKEND_URL}/api/postoperatorios/documents/${doc.id}/pdf`,
      filename: `postoperatorio_${doc.patient_name || doc.id}.pdf`,
      title: `Postoperatorio · ${doc.patient_name || ''}`,
    });
  };

  const onDeleteDoc = async (id) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      await api.delete(`/api/postoperatorios/documents/${id}`);
      toast.success('Eliminado');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const onDeleteTemplate = async (id) => {
    if (!window.confirm('¿Desactivar esta plantilla? Los documentos ya creados seguirán funcionando.')) return;
    try {
      await api.delete(`/api/postoperatorios/templates/${id}`);
      toast.success('Plantilla desactivada');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Postoperatorios"
        subtitle="Plantillas y documentos editables de instrucciones tras la cirugía"
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setNewDocOpen(true)}
              data-testid="postop-new-doc-button"
            >
              <Plus className="w-4 h-4 mr-1" /> Nuevo postoperatorio
            </Button>
            {mgmt && (
              // Qué deja herida y qué productos solo se usan con ella: de
              // cada clínica, en la base (VocabularioHeridaDialog).
              <Button variant="outline" onClick={() => setVocabularioOpen(true)} data-testid="postop-vocabulario-herida">
                Herida y productos
              </Button>
            )}
            {mgmt && (
              <Button
                onClick={() => { setEditingTpl(null); setTplDialogOpen(true); }}
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                data-testid="postop-new-template-button"
              >
                <FileEdit className="w-4 h-4 mr-1" /> Nueva plantilla
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MiniStat label="Plantillas activas" value={templates.length} icon={FileEdit} />
        <MiniStat label="Documentos generados" value={docs.length} icon={FileText} />
        <MiniStat label="Pacientes con postop" value={new Set(docs.map(d => d.patient_id)).size} icon={ListChecks} />
      </div>

      <Tabs defaultValue="documents" className="w-full">
        <TabsList>
          <TabsTrigger value="documents" data-testid="postop-tab-documents">Documentos generados</TabsTrigger>
          <TabsTrigger value="templates" data-testid="postop-tab-templates">Plantillas</TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="mt-4">
          <div className="mb-3 relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por paciente o cirugía…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="postop-search"
            />
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
            </div>
          ) : filteredDocs.length === 0 ? (
            <EmptyState
              icon={ListChecks}
              title="Sin documentos"
              description="Los postoperatorios se crean automáticamente al generar documentación de quirófano, o manualmente desde el botón 'Nuevo postoperatorio'."
            />
          ) : (
            <div className="cp-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-muted/50">
                  <tr>
                    <th className="py-2.5 px-3">Paciente</th>
                    <th className="py-2.5 px-3">Cirugía</th>
                    <th className="py-2.5 px-3">Fecha cirugía</th>
                    <th className="py-2.5 px-3">Plantilla</th>
                    <th className="py-2.5 px-3">Personalizado</th>
                    <th className="py-2.5 px-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocs.map((d) => (
                    <tr key={d.id} className="border-t border-border hover:bg-muted/30" data-testid="postop-doc-row">
                      <td className="py-2.5 px-3 font-medium text-[#0B1220]">{d.patient_name}</td>
                      <td className="py-2.5 px-3 text-muted-foreground">{d.surgery_name || '—'}</td>
                      <td className="py-2.5 px-3 text-muted-foreground">{formatDate(d.surgery_date) || '—'}</td>
                      <td className="py-2.5 px-3 text-muted-foreground">{d.template_name || '—'}</td>
                      <td className="py-2.5 px-3">
                        {d.custom_sections && d.custom_sections.length > 0
                          ? <span className="text-[11px] uppercase tracking-wider bg-[#2E6E5E]/15 text-[#13231E] px-2 py-0.5 rounded">Editado</span>
                          : <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Plantilla</span>
                        }
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => { setEditingDoc(d); setDocDialogOpen(true); }} title="Personalizar contenido" data-testid="postop-doc-edit">
                            <FilePen className="w-4 h-4" />
                          </Button>
                          <PdfLangButton
                            baseUrl={`${BACKEND_URL}/api/postoperatorios/documents/${d.id}/pdf`}
                            filename={`postoperatorio_${d.patient_name || d.id}.pdf`}
                            title={`Postoperatorio · ${d.patient_name || ''}`}
                            onOpen={(p) => setPreview({ open: true, ...p })}
                            dataTestid="postop-doc-pdf"
                          >
                            <Button size="sm" variant="ghost" title="Ver PDF (elige idioma)">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </PdfLangButton>
                          <Button size="sm" variant="ghost" onClick={() => onDeleteDoc(d.id)} title="Eliminar" data-testid="postop-doc-delete">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
            </div>
          ) : templates.length === 0 ? (
            <EmptyState icon={FileEdit} title="Sin plantillas" description="Crea una nueva plantilla con el botón superior." />
          ) : (
            <div className="grid gap-3">
              {templates.map((t) => (
                <div key={t.id} className="cp-card p-4" data-testid="postop-tpl-row">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-semibold text-[#13231E]">{t.name}</div>
                        {t.is_default && <span className="text-[10px] uppercase tracking-wider bg-[#2E6E5E]/15 text-[#13231E] border border-[#2E6E5E]/30 px-1.5 py-0.5 rounded">Por defecto</span>}
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.code}</span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {(t.sections || []).length} secciones · {(t.sections || []).reduce((acc, s) => acc + (s.items?.length || 0), 0)} líneas
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {(t.sections || []).map((s, i) => (
                          <span key={`tpl-${t.id}-sec-${i}`} className="text-[11px] bg-muted/60 px-2 py-0.5 rounded">{s.title}</span>
                        ))}
                      </div>
                    </div>
                    {mgmt && (
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => { setEditingTpl(t); setTplDialogOpen(true); }} data-testid="postop-tpl-edit">
                          <FilePen className="w-4 h-4 mr-1" /> Editar
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onDeleteTemplate(t.id)} data-testid="postop-tpl-delete">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <TemplateEditorDialog
        open={tplDialogOpen}
        onOpenChange={setTplDialogOpen}
        initial={editingTpl}
        onSaved={fetchAll}
      />
      <DocumentEditorDialog
        open={docDialogOpen}
        onOpenChange={setDocDialogOpen}
        doc={editingDoc}
        templates={templates}
        onSaved={fetchAll}
      />
      <NewDocumentDialog
        open={newDocOpen}
        onOpenChange={setNewDocOpen}
        templates={templates}
        surgeons={surgeons}
        patients={patients}
        onSaved={fetchAll}
      />
      <VocabularioHeridaDialog open={vocabularioOpen} onOpenChange={setVocabularioOpen} />
      <PdfPreviewDialog
        open={preview.open}
        onOpenChange={(v) => setPreview(s => ({ ...s, open: v }))}
        url={preview.url}
        filename={preview.filename}
        title={preview.title}
      />
    </div>
  );
}
